import './bufferPolyfill';
import { Buffer as BufferImpl } from 'buffer';
import * as bip39 from 'bip39';
import ENGLISH_WORDLIST_JSON from 'bip39/src/wordlists/english.json';

const BIP39_SAMPLE =
  'abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon about';

/** Webpack 导入 JSON 可能是数组或 { default: [] } */
function unwrapWordlist(raw) {
  if (Array.isArray(raw) && raw.length === 2048) {
    return raw;
  }
  if (raw && Array.isArray(raw.default) && raw.default.length === 2048) {
    return raw.default;
  }
  return null;
}

function resolveEnglishWordlist() {
  const fromJson = unwrapWordlist(ENGLISH_WORDLIST_JSON);
  if (fromJson) {
    return fromJson;
  }
  const fromPackage = unwrapWordlist(bip39.wordlists?.english);
  if (fromPackage) {
    return fromPackage;
  }
  return [];
}

/** 英文 BIP39 词表（2048），供校验与联想 */
export const BIP39_WORDLIST = resolveEnglishWordlist();

try {
  if (bip39.wordlists?.english?.length === 2048) {
    bip39.setDefaultWordlist('english');
  }
} catch (e) {
  console.warn('[mnemonic] bip39.setDefaultWordlist(english) failed', e);
}

const VALID_LENGTHS = [12, 15, 18, 21, 24];

/**
 * 规范化助记词：统一空格/换行/逗号分隔，去掉序号前缀与首尾标点
 */
export function normalizeMnemonic(phrase) {
  let s = String(phrase || '');
  s = s.normalize('NFKD');
  s = s.replace(/[\uFEFF\u200B-\u200D\u2060]/g, '');
  s = s.replace(/[\u00a0\u2000-\u200b\u202f\u205f\u3000]/g, ' ');
  s = s.replace(/[,，;；、]/g, ' ');
  s = s.replace(/[\r\n\t]+/g, ' ');
  s = s.trim().toLowerCase();
  s = s.replace(/\s+/g, ' ');

  const words = s
    .split(' ')
    .map(w =>
      w
        .replace(/^\d+[.):：-]*/, '')
        .replace(/^[^a-z]+/, '')
        .replace(/[^a-z]+$/, '')
    )
    .filter(Boolean);

  return words.join(' ');
}

export function joinMnemonicParts(...parts) {
  const tokens = [];
  for (const part of parts) {
    if (part == null) continue;
    const text = String(part).trim();
    if (!text) continue;
    const normalized = normalizeMnemonic(text);
    if (normalized) {
      tokens.push(...normalized.split(' '));
    }
  }
  return tokens.join(' ');
}

function levenshteinDistance(a, b) {
  const matrix = [];
  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }
  return matrix[b.length][a.length];
}

export function validateWord(word) {
  const lowerWord = word.toLowerCase().trim().replace(/[^a-z]+$/, '').replace(/^[^a-z]+/, '');

  if (BIP39_WORDLIST.includes(lowerWord)) {
    return { valid: true, word: lowerWord };
  }

  const suggestions = [];
  for (const dictWord of BIP39_WORDLIST) {
    const distance = levenshteinDistance(lowerWord, dictWord);
    if (distance <= 2) {
      suggestions.push({ word: dictWord, distance });
    }
  }

  suggestions.sort((a, b) => a.distance - b.distance);

  return {
    valid: false,
    word: lowerWord,
    suggestions: suggestions.slice(0, 5).map(s => s.word)
  };
}

/** 使用 bip39.validateMnemonic 校验 checksum */
export function isValidBip39Checksum(normalizedPhrase) {
  if (!normalizedPhrase || BIP39_WORDLIST.length !== 2048) {
    return false;
  }
  return bip39.validateMnemonic(normalizedPhrase, BIP39_WORDLIST);
}

/**
 * 完整 BIP39 校验：词表 + 标准长度 + bip39.validateMnemonic
 */
export function validateMnemonic(phrase) {
  const normalized = normalizeMnemonic(phrase);
  const words = normalized ? normalized.split(' ') : [];
  const results = words.map(word => validateWord(word));
  const allWordsInList = words.length > 0 && results.every(r => r.valid);
  const validCount = results.filter(r => r.valid).length;
  const lengthOk = VALID_LENGTHS.includes(words.length);

  let error = null;
  if (!words.length) {
    error = 'empty';
  } else if (!lengthOk) {
    error = 'invalid_length';
  } else if (!allWordsInList) {
    error = 'invalid_words';
  } else if (!isValidBip39Checksum(normalized)) {
    error = 'invalid_checksum';
  }

  const checksumValid =
    allWordsInList && lengthOk && isValidBip39Checksum(normalized);

  return {
    valid: checksumValid,
    normalized,
    wordCount: words.length,
    validWords: validCount,
    results,
    allWordsInList,
    lengthOk,
    checksumValid,
    error
  };
}

/** 诊断信息（自检失败时输出） */
export function getBip39Diagnostics() {
  let validateSample = false;
  let validateError = '';
  try {
    validateSample = bip39.validateMnemonic(BIP39_SAMPLE, BIP39_WORDLIST);
    if (!validateSample) {
      try {
        bip39.mnemonicToEntropy(BIP39_SAMPLE, BIP39_WORDLIST);
      } catch (e) {
        validateError = e && e.message ? e.message : String(e);
      }
    }
  } catch (e) {
    validateError = e && e.message ? e.message : String(e);
  }
  return {
    wordlistLength: BIP39_WORDLIST.length,
    hasPackageEnglish: !!(bip39.wordlists && bip39.wordlists.english),
    packageEnglishLength: bip39.wordlists?.english?.length || 0,
    hasBuffer: typeof BufferImpl.from === 'function',
    validateSample,
    validateError
  };
}

/** 启动自检：词表 2048 + bip39.validateMnemonic 标准句 */
export function assertBip39EngineReady() {
  if (BIP39_WORDLIST.length !== 2048) {
    return false;
  }
  return bip39.validateMnemonic(BIP39_SAMPLE, BIP39_WORDLIST);
}

export function getMnemonicSuggestionsByPrefix(prefix, limit = 5) {
  const input = (prefix || '').toLowerCase().trim();
  if (!input) return [];

  const matches = BIP39_WORDLIST.filter(word => word.startsWith(input));
  if (!limit || limit <= 0) return matches;
  return matches.slice(0, limit);
}
